import React from "react"
import Header from "../Header"
import Footer from "../Footer"
import WhatsAppFloat from "../WhatsAppFloat"
import { FileText, Shield, Users, Lock, AlertTriangle, Settings, Mail, Globe, Building, Scale } from "lucide-react"

export default function Terms() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white text-black">
      <Header />

      <main className="pt-28 md:pt-32 pb-16">
        <div className="w-full px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            {/* Header Section */}
            <div className="text-center mb-12">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-6">
                <FileText className="w-8 h-8 text-blue-600" />
              </div>
              <h1 className="text-4xl md:text-5xl font-bold mb-4 bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent">
                Terms of Service
              </h1>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                Please read these terms carefully before using our services.
              </p>
              <div className="mt-6 inline-flex items-center gap-2 px-4 py-2 bg-gray-100 rounded-full text-sm text-gray-600">
                <FileText className="w-4 h-4" />
                <span>Effective Date: 10/31/25</span>
              </div>
            </div>

            {/* Introduction */}
            <div className="bg-white rounded-2xl border border-gray-200 p-8 mb-8 shadow-sm">
              <p className="text-lg text-gray-800 leading-relaxed">
                These Terms of Service govern your use of TrackFam and its services. By accessing or using our mobile application, you agree to be bound by these terms and all applicable laws. If you do not agree to any part of these terms, you may not use our services.
              </p>
            </div>

            {/* Acceptance of Terms */}
            <section className="mb-10">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                  <Shield className="w-5 h-5 text-green-600" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900">1. Acceptance of Terms</h2>
              </div>
              <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm">
                <p className="text-gray-800">
                  By accessing or using the TrackFam mobile application or services, you agree to be bound by these Terms of Service and all applicable laws. If you do not agree to any part of these terms, you may not use our services.
                </p>
              </div>
            </section>

            {/* Service Description */}
            <section className="mb-10">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Settings className="w-5 h-5 text-blue-600" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900">2. Service Description</h2>
              </div>
              <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm">
                <p className="text-gray-800">
                  TrackFam provides a mobile application that enables families to track their loved ones' real-time location, receive place alerts, monitor battery status, and maintain family safety through secure location sharing. We reserve the right to modify, suspend or discontinue the service at any time without notice.
                </p>
              </div>
            </section>

            {/* User Accounts and Responsibilities */}
            <section className="mb-10">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                  <Users className="w-5 h-5 text-purple-600" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900">3. User Accounts and Responsibilities</h2>
              </div>
              <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm">
                <p className="text-gray-800">
                  You must create an account to use TrackFam services. You agree to provide accurate, current and complete information and to keep your credentials secure. You are responsible for all activities that occur under your account, including location data sharing with family members you authorize.
                </p>
              </div>
            </section>

            {/* Intellectual Property */}
            <section className="mb-10">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
                  <Scale className="w-5 h-5 text-indigo-600" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900">4. Intellectual Property</h2>
              </div>
              <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm">
                <p className="text-gray-800">
                  All content, logos, software and design elements of TrackFam remain our property. You retain ownership of your location data and personal information. We only use your data to provide location tracking services and will never share or sell your data to third parties. All data is encrypted end-to-end for your privacy and security.
                </p>
              </div>
            </section>

            {/* Prohibited Activities */}
            <section className="mb-10">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                  <AlertTriangle className="w-5 h-5 text-red-600" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900">5. Prohibited Activities</h2>
              </div>
              <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm">
                <p className="text-gray-800">
                  You agree not to use our service to break the law, stalk or harm others, violate anyone's privacy, share location data without consent, infringe any intellectual property rights, or interfere with the operation of our platform. Location tracking must only be used for family safety purposes with proper consent. We reserve the right to suspend accounts for violations.
                </p>
              </div>
            </section>

            {/* Termination */}
            <section className="mb-10">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                  <Lock className="w-5 h-5 text-orange-600" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900">6. Termination</h2>
              </div>
              <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm">
                <p className="text-gray-800">
                  We may suspend or terminate your access if you violate these terms or act in a way that may harm the service, other users, or misuse location tracking features. You may stop using the service at any time by deleting your account. Termination may result in deletion of your location history and account information.
                </p>
              </div>
            </section>

            {/* Disclaimer and Limitation of Liability */}
            <section className="mb-10">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-yellow-100 rounded-lg flex items-center justify-center">
                  <AlertTriangle className="w-5 h-5 text-yellow-600" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900">7. Disclaimer and Limitation of Liability</h2>
              </div>
              <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm">
                <p className="text-gray-800">
                  Your use of the service is at your own risk. TrackFam is provided "as is" and "as available," without warranties of any kind. Location accuracy depends on GPS, Wi-Fi, and cellular signals. We are not liable for any indirect, incidental or consequential damages arising from your use of the service, including inaccurate location data.
                </p>
              </div>
            </section>

            {/* Changes to Terms */}
            <section className="mb-10">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center">
                  <FileText className="w-5 h-5 text-teal-600" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900">8. Changes to Terms</h2>
              </div>
              <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm">
                <p className="text-gray-800">
                  We may update these Terms of Service at our discretion. Material changes will be posted on this page, and continued use of the service constitutes your acceptance of the revised terms.
                </p>
              </div>
            </section>

            {/* Governing Law */}
            <section className="mb-10">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                  <Globe className="w-5 h-5 text-green-600" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900">9. Governing Law</h2>
              </div>
              <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm">
                <p className="text-gray-800">
                  These terms are governed by the laws applicable in your jurisdiction. For users in the United Arab Emirates, any disputes will be subject to the jurisdiction of the courts in Dubai.
                </p>
              </div>
            </section>

            {/* Contact Information */}
            <section className="mb-10">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Mail className="w-5 h-5 text-blue-600" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900">10. Contact Information</h2>
              </div>
              <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm">
                <p className="text-gray-800 mb-4">If you have questions about these terms, please contact us at:</p>
                <div className="space-y-3">
                  <div className="flex items-center gap-3 p-3 bg-blue-50 rounded-lg">
                    <Mail className="w-5 h-5 text-blue-600" />
                    <span className="text-blue-800 font-medium">support@trackfam.app</span>
                  </div>
                  <div className="flex items-center gap-3 p-3 bg-green-50 rounded-lg">
                    <Globe className="w-5 h-5 text-green-600" />
                    <span className="text-green-800 font-medium">https://www.trackfam.app</span>
                  </div>
                  <div className="flex items-center gap-3 p-3 bg-purple-50 rounded-lg">
                    <Building className="w-5 h-5 text-purple-600" />
                    <span className="text-purple-800 font-medium">Worldwide Service</span>
                  </div>
                </div>
              </div>
            </section>

            {/* Footer Note */}
            <div className="text-center mt-12 p-6 bg-gray-50 rounded-2xl border border-gray-200">
              <p className="text-gray-600 text-sm">
                Thank you for using TrackFam to keep your loved ones safe and connected. By using our services, you acknowledge that you have read, understood, and agree to be bound by these Terms of Service.
              </p>
            </div>
          </div>
        </div>
      </main>

      <Footer />
      <WhatsAppFloat />
    </div>
  )
}
